import * as THREE from 'three';
import { ColoredObjectSchema } from './ColoredObject';
import { GlintEffect } from './glint';
import { SelectableObject } from './selectableObject';
import { stringSchema } from '@/data/common';
import z from 'zod';

export const SlopeCornerSchema = ColoredObjectSchema.extend({
  name: stringSchema.default('SlopeCorner'),
});

export class SlopeCorner extends SelectableObject<typeof SlopeCornerSchema> {
  static override schema = SlopeCornerSchema;

  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshStandardMaterial;
  private readonly glintEffect: GlintEffect;

  constructor(options: z.input<typeof SlopeCornerSchema> = {}) {
    super(options);

    this.material = new THREE.MeshStandardMaterial({
      color: this.data.color,
      emissive: 0x000000,
      roughness: 0.35,
      metalness: 0.05,
    });

    const geometry = new THREE.BufferGeometry();

    // Define vertices for the slope corner
    /* eslint-disable prettier/prettier */
    const vertices = new Float32Array([
      // Base rectangle (x-yz)
      -0.5, -0.5, -0.5, // 0: -x-y-z
       0.5, -0.5, -0.5, // 1: +x-y-z
       0.5, -0.5,  0.5, // 2: +x-y+z
      -0.5, -0.5,  0.5, // 3: -x-y+z

      // Top vertex for slopes
      0.5, 0.5, 0.5, // 4: +x+y+z
    ]);

    // Define faces (triangles) using vertex indices
    const indices = [
      // -y face
      0, 1, 2, 0, 2, 3,
      // -x slope
      0, 3, 4,
      // -z slope
      1, 0, 4,
      // +z face
      3, 2, 4,
      // +x face
      2, 1, 4,
    ];
    /* eslint-enable prettier/prettier */

    // Set the geometry's attributes
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(indices);

    this.mesh = new THREE.Mesh(geometry, this.material);

    this.glintEffect = new GlintEffect(this.material);
  }

  protected renderObject(parent: THREE.Object3D) {
    parent.add(this.mesh);
  }

  override getPickTargets() {
    return [this.mesh];
  }

  override getAabb() {
    return new THREE.Box3().setFromCenterAndSize(
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(1, 1, 1),
    );
  }

  override update(delta: number): void {
    this.glintEffect.update(delta);
  }

  override dispose() {
    super.dispose();
    this.glintEffect.dispose();
    this.mesh.geometry.dispose();
    this.material.dispose();
  }

  protected override onSelectionChanged(selected: boolean): void {
    this.glintEffect.setSelected(selected);
  }
}
